import { cn } from "@/components/podkit/lib/cn";
import type React from "react";
import { forwardRef, useMemo } from "react";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";

export type TextShimmerProps = PropsWithTestId & {
    children: string;
    className?: string;
    duration?: number;
    spread?: number;
    // Can be used to disable shimmering
    shimmer?: boolean;
};

/**
 * This adds a shimmer effect to the text.
 *
 * The implementation is based on the following component:
 * https://github.com/ibelick/motion-primitives/blob/main/components/core/text-shimmer.tsx
 *
 * With the following changes:
 * - We don't use Motion (motion.dev) yet so we're relying on a custom Tailwind animation to
 *   animate the background position.
 * - Colors have been updated to match our color tokens.
 */
export const TextShimmer = forwardRef<HTMLParagraphElement, TextShimmerProps>(
    ({ children, className, spread = 2, shimmer = true, ...props }, ref) => {
        const dynamicSpread = useMemo(() => {
            return children.length * spread;
        }, [children, spread]);

        return (
            <p
                ref={ref}
                className={cn(
                    "relative bg-[length:250%_100%,auto] bg-clip-text",
                    { "animate-text-shimmer": shimmer },
                    "text-transparent",
                    "[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),rgb(var(--content-primary)),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
                    "dark:[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),rgb(var(--content-primary)),#0000_calc(50%+var(--spread)))]",
                    className,
                )}
                style={
                    {
                        "--spread": `${dynamicSpread}px`,
                        backgroundImage: `var(--bg), linear-gradient(rgb(var(--content-tertiary)), rgb(var(--content-tertiary)))`,
                    } as React.CSSProperties
                }
                {...props}
            >
                {children}
            </p>
        );
    },
);

TextShimmer.displayName = "TextShimmer";
